import { useState, useEffect, useRef } from "react";
import { Bell } from "lucide-react";
import { request } from "../../utils/api";
import { STORAGE_KEYS } from "../../utils/constants";
import {
  initAudioContext,
  playAccessRequestChime,
  playAccessGrantedChime,
  playAccessRejectedChime,
} from "../../utils/audioUtils";
import { useNotificationSummary } from '../../notifications/NotificationSummaryProvider';

// Notification types that get an audible chime. Hoisted out of the fetch
// handler so the websocket path and the summary path use one list.
const SOUNDED_NOTIFICATION_TYPES = new Set([
  "ACCESS_REQUEST", "ACCESS_GRANTED", "ACCESS_REJECTED", "ACCESS_REVOKED",
  "PRD_READY", "DB_SCHEMA_READY", "DESIGN_COMPLETE", "API_CONTRACT_READY",
  "MOCK_API_DEPLOYED", "MOCK_API_ENRICHED", "TEST_CASES_READY",
  "STAGE_COMPLETED", "BUG_REPORTED",
]);

const NotificationBell = ({ onClick }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const lastNotificationIdRef = useRef(0);
  const audioInitializedRef = useRef(false);
  const wsRef = useRef(null);

  useEffect(() => {
    // Initialize audio context lazily (first user gesture should enable it)
    if (!audioInitializedRef.current) {
      initAudioContext();
      audioInitializedRef.current = true;
    }

    // No initial fetch here: NotificationSummaryProvider already loads the
    // unread list on mount, and calling it again would duplicate that request
    // on every page load. fetchUnreadNotifications is still used by the
    // websocket handler below, which needs to refetch when a push arrives.

    // Open websocket for real-time updates
    openWebSocket();

    // Listen for count updates from NotificationPanel
    const handleCountUpdate = (event) => {
      if (event.detail && typeof event.detail.unreadCount === 'number') {
        setUnreadCount(event.detail.unreadCount);
      }
    };
    window.addEventListener('notification-count-updated', handleCountUpdate);

    return () => {
      window.removeEventListener('notification-count-updated', handleCountUpdate);
      // close websocket
      if (wsRef.current) {
        try { wsRef.current.close(); } catch (e) { }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Count and unread list both come from the shared summary poll, so this
  // component no longer runs a timer of its own. fetchUnreadNotifications is
  // kept for the websocket path, which still needs to refetch on a push.
  // This is the ONLY bell in the app: the badge counts platform AND workflow
  // notifications together, and the panel it opens shows both streams.
  const { platform, totalUnreadCount } = useNotificationSummary();

  // Chime for anything that arrived since the last summary we saw. Same rule as
  // before: the first load only establishes a baseline, so signing in does not
  // replay every unread notification as a burst of sounds.
  useEffect(() => {
    const notifications = platform.notifications || [];
    setUnreadCount(totalUnreadCount || 0);
    if (notifications.length === 0) return;

    const maxId = notifications.reduce((max, n) => (n && n.id ? Math.max(max, n.id) : max), 0);
    if (lastNotificationIdRef.current === 0) {
      lastNotificationIdRef.current = maxId;
      return;
    }
    if (maxId > lastNotificationIdRef.current) {
      notifications
        .filter((n) => n && n.id && n.id > lastNotificationIdRef.current)
        .sort((a, b) => a.id - b.id)
        .forEach((n) => {
          if (SOUNDED_NOTIFICATION_TYPES.has(n.type)) playNotificationSound(n.type);
        });
      lastNotificationIdRef.current = maxId;
    }
  }, [platform, totalUnreadCount]);

  const playNotificationSound = (type) => {
    if (!audioInitializedRef.current) {
      initAudioContext();
      audioInitializedRef.current = true;
    }
    if (type === "ACCESS_REQUEST") playAccessRequestChime();
    else if (type === "ACCESS_GRANTED") playAccessGrantedChime();
    else if (type === "ACCESS_REJECTED") playAccessRejectedChime();
    else if (type === "ACCESS_REVOKED") playAccessRejectedChime();
    // Workflow notification types — reuse existing chimes
    else if (type === "PRD_READY" || type === "DB_SCHEMA_READY" || type === "DESIGN_COMPLETE" ||
             type === "API_CONTRACT_READY" || type === "MOCK_API_DEPLOYED" || type === "MOCK_API_ENRICHED" ||
             type === "TEST_CASES_READY" || type === "STAGE_COMPLETED") {
      playAccessGrantedChime();
    } else if (type === "BUG_REPORTED") {
      playAccessRequestChime();
    }
  };

  const openWebSocket = () => {
    try {
      const userId = sessionStorage.getItem(STORAGE_KEYS.USERID);
      if (!userId) {
        console.warn('⚠️ No userId found in session, cannot open WebSocket');
        return;
      }
      const protocol = window.location.protocol === "https:" ? "wss" : "ws";
      const wsUrl = `${protocol}://${window.location.host}/ws/notifications?userId=${userId}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
      };

      ws.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          // payload is expected to be the saved notification
          if (payload && payload.type) {
            
            // Handle ACCESS_REVOKED specially for real-time project access removal
            if (payload.type === 'ACCESS_REVOKED' && payload.related_project_id) {
              // Dispatch custom event for project access revocation
              window.dispatchEvent(new CustomEvent('project-access-revoked', {
                detail: {
                  projectId: payload.related_project_id,
                  projectName: payload.related_project_name,
                  message: payload.message
                }
              }));
            }
            
            // increment unread count and play sound
            setUnreadCount((prev) => prev + 1);
            playNotificationSound(payload.type);
            // update baseline id
            if (payload.id) lastNotificationIdRef.current = Math.max(lastNotificationIdRef.current, payload.id);
            // notify other components (NotificationPanel listens for this)
            window.dispatchEvent(new CustomEvent('new-notification', { detail: payload }));
          }
        } catch (e) {
          console.error('❌ Invalid websocket message', e);
        }
      };

      ws.onclose = (event) => {
        // try reconnect after a delay
        setTimeout(() => openWebSocket(), 5000);
      };

      ws.onerror = (err) => {
        console.error('❌ WebSocket error:', err);
        try { ws.close(); } catch (e) { }
      };
    } catch (e) {
      console.error('Failed to open websocket', e);
    }
  };

  const fetchUnreadNotifications = async () => {
    try {
      // Prefer fetching unread notifications so we can detect type
      const res = await request("/api/notifications?unreadOnly=true", "GET");
      if (res && res.success) {
        const notifications = res.data || [];
        const unread = Array.isArray(notifications) ? notifications.length : 0;
        setUnreadCount(res.unread_count ?? unread);

        if (notifications.length > 0) {
          // Find the latest notification id (assuming `id` numeric)
          const maxId = notifications.reduce((max, n) => (n && n.id ? Math.max(max, n.id) : max), 0);
          if (lastNotificationIdRef.current === 0) {
            // first load - set baseline but do not play sound
            lastNotificationIdRef.current = maxId;
          } else if (maxId > lastNotificationIdRef.current) {
            // new notifications arrived
            const newOnes = notifications.filter(n => n && n.id && n.id > lastNotificationIdRef.current)
              .sort((a, b) => a.id - b.id);
            // play sound for each new notification of interest
            newOnes.forEach(n => {
              if (n.type === "ACCESS_REQUEST" || n.type === "ACCESS_GRANTED" || n.type === "ACCESS_REJECTED" || n.type === "ACCESS_REVOKED"
                  || n.type === "PRD_READY" || n.type === "DB_SCHEMA_READY" || n.type === "DESIGN_COMPLETE"
                  || n.type === "API_CONTRACT_READY" || n.type === "MOCK_API_DEPLOYED" || n.type === "MOCK_API_ENRICHED"
                  || n.type === "TEST_CASES_READY" || n.type === "STAGE_COMPLETED" || n.type === "BUG_REPORTED") {
                playNotificationSound(n.type);
              }
            });
            lastNotificationIdRef.current = maxId;
          }
        }
      } else {
        // fallback to unread-count endpoint
        const countRes = await request("/api/notifications/unread-count", "GET");
        if (countRes && countRes.success) setUnreadCount(countRes.unread_count || 0);
      }
    } catch (error) {
      console.error("Error fetching unread notifications:", error);
    }
  };

  return (
    <button
      onClick={onClick}
      className="
        relative p-2 rounded-md
        text-foreground-secondary hover:text-foreground hover:bg-card
        transition-colors duration-200
        focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background
      "
      aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
    >
      <Bell className="w-5 h-5" />
      {unreadCount > 0 && (
        <span className="
          absolute top-0 right-0
          w-5 h-5 flex items-center justify-center
          text-xs font-bold
          bg-danger text-white
          rounded-full
          shadow-sm
        ">
          {unreadCount > 99 ? "99+" : unreadCount}
        </span>
      )}
    </button>
  );
};

export default NotificationBell;
